import type { RepBucket, SetStatus } from '@lift2fit/shared-types';
import { epley1rm, repBucket } from './units';

/** `${exerciseId}:${repBucket}` -> best estimated 1RM in kilograms */
export type BestMap = Record<string, number>;

export interface PrCandidate {
  exerciseId: string;
  weightKg: number;
  reps: number;
  status: SetStatus;
  isWarmup: boolean;
}

export interface PrVerdict {
  isPr: boolean;
  e1rm: number;
  bucket: RepBucket;
  previousBest: number | null;
  deltaKg: number;
}

export function bestKey(exerciseId: string, bucket: RepBucket): string {
  return `${exerciseId}:${bucket}`;
}

/**
 * A set is a personal record when its estimated 1RM beats the best previously
 * recorded effort in the same rep bucket. Mirrors public.flag_personal_record().
 */
export function detectPr(
  candidate: { exerciseId: string; weightKg: number; reps: number },
  bests: BestMap,
): PrVerdict {
  const bucket = repBucket(candidate.reps);
  const e1rm = epley1rm(candidate.weightKg, candidate.reps);
  const previous = bests[bestKey(candidate.exerciseId, bucket)] ?? null;
  const isPr = e1rm > 0 && (previous === null || e1rm > previous);
  return {
    isPr,
    e1rm,
    bucket,
    previousBest: previous,
    deltaKg: previous === null ? 0 : Math.round((e1rm - previous) * 100) / 100,
  };
}

/** Fold committed sets into a copy of the base best map (does not mutate `base`). */
export function foldBests(sets: PrCandidate[], base: BestMap = {}): BestMap {
  const next: BestMap = { ...base };
  for (const set of sets) {
    if (set.isWarmup || set.status === 'failed' || set.reps <= 0) continue;
    const bucket = repBucket(set.reps);
    const key = bestKey(set.exerciseId, bucket);
    const e1rm = epley1rm(set.weightKg, set.reps);
    if (e1rm > (next[key] ?? 0)) next[key] = e1rm;
  }
  return next;
}

/** Re-evaluate the PR flag of every set in a session in chronological order. */
export function recomputePrFlags<T extends PrCandidate & { isPr: boolean }>(
  setsInOrder: T[],
  base: BestMap = {},
): T[] {
  const running: BestMap = { ...base };
  return setsInOrder.map((set) => {
    if (set.isWarmup || set.status === 'failed' || set.reps <= 0) {
      return { ...set, isPr: false };
    }
    const verdict = detectPr(set, running);
    const bucket = repBucket(set.reps);
    const key = bestKey(set.exerciseId, bucket);
    if (verdict.isPr) running[key] = verdict.e1rm;
    return { ...set, isPr: verdict.isPr };
  });
}
